import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, LogIn } from 'lucide-react';

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to={location.state?.from?.pathname || '/dashboard'} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      {/* Left form */}
      <div className="flex items-center justify-center p-6 md:p-10">
        <Card className="w-full max-w-md border-border/70" data-testid="login-card">
          <CardHeader className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-md flex items-center justify-center" style={{ backgroundColor: 'hsl(var(--primary))' }}>
                <ShieldCheck className="h-4 w-4 text-white" />
              </div>
              <span className="font-display text-base font-semibold">MSME Ops</span>
            </div>
            <CardTitle className="font-display text-2xl">Sign in to your workspace</CardTitle>
            <CardDescription>Access your tasks, reports, and dashboards.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@msme.io" autoComplete="email" data-testid="login-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" data-testid="login-password-input" />
              </div>
              <Button type="submit" className="w-full" disabled={submitting} data-testid="login-submit-button">
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogIn className="h-4 w-4 mr-2" />}
                Sign in
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-card px-2 text-muted-foreground">or</span>
              </div>
            </div>

            <Button variant="outline" className="w-full" onClick={googleLogin} data-testid="login-google-button">
              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.96l3.66-2.84z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335" />
              </svg>
              Continue with Google
            </Button>

            <div className="pt-2 text-xs text-muted-foreground text-center">
              Need access? Ask your Admin to create your account.
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right texture panel */}
      <div className="hidden md:flex relative overflow-hidden paper-wash">
        <div className="m-auto max-w-md px-8 py-10">
          <div className="text-sm font-medium text-primary">Micro‑MSME Task Management</div>
          <h2 className="font-display text-3xl font-semibold mt-2 leading-tight">Ship focused work,<br />track it end‑to‑end.</h2>
          <p className="text-sm text-muted-foreground mt-4">A calm operational hub for admins, managers, and interns — with time‑bound access, daily reports, and downloadable weekly summaries.</p>
          <ul className="mt-6 space-y-2 text-sm text-foreground/80">
            <li>• Role‑based dashboards for 6 roles</li>
            <li>• Kanban tasks with subtasks + comments</li>
            <li>• Weekly PDF & Excel exports</li>
            <li>• Time‑bound intern accounts</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
